import React, { useState } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input/mobile';
import { yupResolver } from '@hookform/resolvers/yup';
import { useStytch } from '@stytch/nextjs';
import * as yup from 'yup';
import { CountryCode } from 'libphonenumber-js';
import { useRouter } from 'next/router';

import GenericModal from '../modals/GenericModal';
import Button from 'components/app-components/ui/button';
import { OTP_EXPIRATION_MINUTES } from 'lib/constants';
import { isCountrySupportedForOtpMethod } from 'lib/countryList';
import { useUser } from 'context/user';
import theraToast from 'components/generic-components/toast/TheraToast';
import { MFAMethodType } from 'types/mfa';
import { AuthRoutes } from 'routes';
import { getPhoneOTPErrorMessage, getUnsupportedCountryMessage } from './mfaMessages';

interface IFormValues {
  phone: string;
}

const handlePhoneValidation = (input: string): boolean => {
  const isValid = isValidPhoneNumber(input);
  return isValid;
};
const schema: any = yup.object().shape({
  phone: yup
    .string()
    .trim()
    .required('Business Phone is required')
    .test('isPhoneValid', 'Enter a valid phone number', (value) => handlePhoneValidation(value)),
});

const PhoneOTPModal = ({
  open,
  onClose,
  setPhoneMethodID,
  openOTPModal,
  userPhoneNumber,
  method,
  // userPhoneNumberVerified,
}: {
  open: boolean;
  onClose: () => void;
  setPhoneMethodID: (methodID: string) => void;
  openOTPModal: () => void;
  userPhoneNumber: string;
  userPhoneNumberVerified: boolean;
  method: MFAMethodType;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();
  const userCountry = user?.employeeInformation?.address?.country || 'US';
  const stytchClient = useStytch();
  const router = useRouter();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IFormValues>({
    defaultValues: {
      phone: userPhoneNumber,
    },
    resolver: yupResolver(schema) as Resolver<IFormValues>,
  });

  const phoneNumber = watch('phone');

  const onSubmit: SubmitHandler<IFormValues> = async (data) => {
    // The 2FA method list is filtered by the country on the user's profile, but the number typed
    // here can belong to another one. Without this check Stytch is the first to notice.
    const enteredCountry = parsePhoneNumber(data.phone)?.country;
    if (enteredCountry && !isCountrySupportedForOtpMethod(method, enteredCountry)) {
      theraToast.warning(getUnsupportedCountryMessage(method), { id: 'phone-otp-modal-error' });
      return;
    }

    setIsSubmitting(true);
    try {
      let res;
      if (method === MFAMethodType.SMS) {
        res = await stytchClient.otps.sms.send(data.phone, {
          expiration_minutes: OTP_EXPIRATION_MINUTES,
        });
      } else {
        res = await stytchClient.otps.whatsapp.send(data.phone, {
          expiration_minutes: OTP_EXPIRATION_MINUTES,
        });
      }
      setPhoneMethodID(res.method_id);
      onClose();
      openOTPModal();
    } catch (error) {
      const errorMessage = getPhoneOTPErrorMessage(error.error_type, method, 'Something went wrong. Please try again.');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'phone-otp-modal-error',
        },
      });
      if (error.error_type === 'user_unauthenticated') {
        setTimeout(() => {
          router.replace(AuthRoutes.SignIn);
        }, 2000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = method === MFAMethodType.SMS ? 'Set up using SMS' : 'Set up using WhatsApp';
  const formLabel = method === MFAMethodType.SMS ? 'Enter your phone number' : 'Enter your WhatsApp number';

  return (
    <GenericModal open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit(onSubmit)} className="min-w-[360px]">
        <div className="mb-3">
          <label htmlFor="phone" className="required form-label">
            {formLabel}
          </label>
          <div className="mt-1">
            <div className="form-input !pl-3" style={{ paddingTop: 0, paddingBottom: 0 }}>
              <PhoneInputWithCountry
                className="business-phone-form-input"
                name="phone"
                control={control}
                limitMaxLength
                defaultCountry={userCountry as CountryCode}
                international
              />
            </div>
            <ErrorMessage errors={errors} name="phone" />
          </div>
        </div>
        <div className="flex">
          <Button htmlType="submit" disabled={!phoneNumber || isSubmitting} className="w-full" loading={isSubmitting}>
            Next
          </Button>
        </div>
      </form>
    </GenericModal>
  );
};

export default PhoneOTPModal;
